"use client";

import React from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { useTransform, type MotionValue } from "framer-motion";

import Chip from "@/components/bastian-keller/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { MotionDiv, MotionSpan } from "@/components/global/utils/motionTags";
import { parseStat, useCountUp } from "@/components/bastian-keller/statValue";
import { useMotionMode } from "@/components/global/utils/motionMode";
import { REVEAL_OFFSET, useReveal, useRevealProgress } from "@/components/global/utils/scrollReveal";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

import noiseA from "@/public/bastian-keller/noise-a.svg";
import noiseB from "@/public/bastian-keller/noise-b.svg";

import component1 from "@/public/home-new/component-1-fallback.png";
import component2 from "@/public/home-new/component-2-fallback.png";
import component3 from "@/public/home-new/component-3-fallback.png";
import component4 from "@/public/home-new/component-4-fallback.png";

// cycled across the cards by index, so a strip longer than the set repeats it
const componentAssets = [
  { playbackID: "P6X95gsZYVmS7Dp9ljaCeFB2I02GTBiWKj5wBYLqf5MY", fallbackImage: component1 },
  { playbackID: "NDa9S3KQdXU49OOKuUP02x5c00jn02fNBdolq9BZ74yGAw", fallbackImage: component2 },
  { playbackID: "OSBosbIRuobW4rARKRrkxlgl01ghgoxq5OAmBj8vEsck", fallbackImage: component3 },
  { playbackID: "gLfal802695sHQfKsEeW00I01q1luCrZYnHVyaXohIV2ZQ", fallbackImage: component4 },
];

// the pixel field fades out over the last quarter of its band, per the Figma mask
const NOISE_FADE = "linear-gradient(to bottom, #000 75.83%, transparent 100%)";

/** the field's resting opacity in the design — where a scrubbed fade has to land */
const NOISE_OPACITY = 0.06;

/** chip, title, CTA */
const HEADER_PARTS = 3;

function SuccessCard({
  item,
  progress,
  index,
  total,
  reduceMotion,
}: {
  item: PageBastianKellerSchema["successItems"][number];
  progress: MotionValue<number>;
  index: number;
  total: number;
  reduceMotion: boolean;
}) {
  const { reveal, y, linear } = useReveal(progress, index, total, reduceMotion);

  const asset = componentAssets[index % componentAssets.length];

  const parsed = parseStat(item?.value);
  const counts = Boolean(parsed) && !reduceMotion;
  const display = useCountUp(linear, parsed);

  const noiseOpacity = useTransform(reveal, [0, 1], [0, NOISE_OPACITY]);

  /*
    The lime rule along the card's top edge is drawn by retracting a patch of the
    section's own background off it, rather than by scaling the rule itself: the
    rule is a border, and the card clips its overflow, so nothing laid over that
    edge from the inside would survive. Covering from outside leaves the card's
    box exactly as the design has it.
  */
  const ruleCover = useTransform(reveal, [0, 1], [1, 0]);

  // the server has no scroll position, so it would render a zero — the authored
  // value stays in the markup until the client takes over
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  return (
    <MotionDiv
      style={{ opacity: reveal, y }}
      className="relative w-[41.625rem] shrink-0 lg:w-auto">
      <div
        /*
          The number block is anchored from the top, not the bottom, so every card
          keeps the same baseline whether its label runs to one line or two —
          matching how they line up in the design.
        */
        className="relative flex h-[49.5rem] w-full flex-col overflow-hidden border-t-[2px] border-t-[#D9F9B9] bg-[#112628] px-[3rem] pt-[36.75rem] lg:h-[65.25rem] lg:px-[3.5rem] lg:pt-[44.875rem]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-[4rem] right-0 left-0 h-[50%] w-full overflow-clip opacity-70 mix-blend-screen">
          <BackgroundVideo
            playbackId={asset.playbackID}
            fallbackImage={asset.fallbackImage}
          />
        </div>
        {/*
          Decorative pixel field bleeding past both card edges, exactly as the
          Figma mask group: two mirrored halves under a fade-out mask.
        */}
        <MotionDiv
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-[-6.5rem] flex h-[11.53rem] w-[54.74rem] lg:left-[-7.625rem] lg:h-[13.5rem] lg:w-[64.07rem]"
          style={{
            opacity: noiseOpacity,
            maskImage: NOISE_FADE,
            WebkitMaskImage: NOISE_FADE,
          }}>
          {[noiseA, noiseB].map((half, halfIndex) => (
            <div
              key={halfIndex}
              className="flex h-[11.53rem] w-[27.37rem] items-center justify-center lg:h-[13.5rem] lg:w-[32.035rem]">
              <div className={halfIndex === 0 ? "rotate-90" : "-rotate-90"}>
                <Image
                  className="!h-[27.37rem] !w-[11.53rem] max-w-none lg:!h-[32.035rem] lg:!w-[13.5rem]"
                  src={half}
                  alt=""
                  quality={100}
                />
              </div>
            </div>
          ))}
        </MotionDiv>

        <div className="relative flex flex-col gap-[2.5rem] lg:w-[35.875rem] lg:gap-[3rem]">
          {item?.value && (
            <p className="m-0 font-funnel text-[5rem] leading-[1.3] tracking-[-0.06em] text-[#D9F9B9] [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] lg:text-[10rem]">
              {counts && parsed && mounted ? (
                <>
                  {parsed.prefix}
                  {/*
                    The MotionValue is this span's only child — framer drives it
                    straight onto textContent, so the figure climbs without
                    re-rendering React. Tabular figures stop the number juddering
                    as digits swap.
                  */}
                  <MotionSpan className="tabular-nums">{display}</MotionSpan>
                  {parsed.suffix}
                </>
              ) : (
                item.value
              )}
            </p>
          )}

          {item?.label && (
            <div className="richtext richtext-start prose-p:font-geist prose-p:text-[2.25rem] prose-p:leading-[1.17] prose-p:text-white/80 lg:prose-p:text-[2.5rem] prose-strong:font-normal prose-strong:text-white/50">
              <PortableText
                value={item.label}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      </div>

      <MotionDiv
        aria-hidden="true"
        style={{ scaleX: ruleCover }}
        className="pointer-events-none absolute inset-x-0 top-0 h-[2px] origin-right bg-[#0A1A1D]"
      />
    </MotionDiv>
  );
}

export default function SuccessSection({ data, className }: { data: PageBastianKellerSchema; className?: string }) {
  const items = data?.successItems;
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const drag = React.useRef({ dragging: false, startX: 0, startScroll: 0 });

  const { reduceMotion } = useMotionMode();

  const headerProgress = useRevealProgress(headerRef, REVEAL_OFFSET, reduceMotion);
  const chip = useReveal(headerProgress, 0, HEADER_PARTS, reduceMotion);
  const title = useReveal(headerProgress, 1, HEADER_PARTS, reduceMotion);
  const cta = useReveal(headerProgress, 2, HEADER_PARTS, reduceMotion);

  /*
    The cards cascade off the strip's vertical travel: the row arrives as one band
    as the section comes up, and the cards deal in along it.
  */
  const stripProgress = useRevealProgress(viewportRef, REVEAL_OFFSET, reduceMotion);

  // drag-to-scroll only matters while the strip is a scroller — on desktop the four
  // cards sit in one row and there is nothing to drag
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    drag.current = {
      dragging: true,
      startX: event.clientX,
      startScroll: viewport.scrollLeft,
    };
    viewport.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    if (!viewport || !drag.current.dragging) return;
    viewport.scrollLeft = drag.current.startScroll - (event.clientX - drag.current.startX);
  };

  const onPointerEnd = () => {
    drag.current.dragging = false;
  };

  return (
    <section className={cn("relative bg-[#0A1A1D]", className)}>
      <div className="relative overflow-clip">
        <div className="relative mx-auto flex w-[min(240rem,100%)] flex-col">
          <DecorationLine
            className="bottom-0 hidden lg:block"
            orientation="horizontal"
            style="light"
          />

          {/*
            The divider under the header hangs off the header's own bottom edge
            rather than sitting at a fixed offset, so it follows the copy however
            many lines the title runs to. The verticals live in here too: in both
            Figma frames they run the header and stop at this rule, leaving the
            card strip below to run clean to the edges.
          */}
          <div className="relative shrink-0">
            <DecorationLine
              className="inset-y-0 left-[1rem] h-auto lg:left-[7.125rem]"
              orientation="vertical"
              style="light"
            />
            <DecorationLine
              className="inset-y-0 right-[1rem] h-auto lg:right-[7.125rem]"
              orientation="vertical"
              style="light"
            />
            <DecorationLine
              className="bottom-0"
              orientation="horizontal"
              style="light"
            />

            <div
              ref={headerRef}
              className="flex flex-col gap-[4rem] px-[2rem] pt-[10rem] pb-[6rem] sm:px-[3rem] md:px-[5rem] lg:flex-row lg:items-end lg:justify-between lg:gap-[6rem] lg:px-[12.5rem] lg:pt-[12.5rem] lg:pb-[5.75rem]">
              <div className="flex flex-col items-start gap-[3rem] lg:w-[103.75rem]">
                {data?.successChip?.text && (
                  <MotionDiv style={{ opacity: chip.reveal, y: chip.y }}>
                    <Chip
                      image={data?.successChip?.icon}
                      text={data?.successChip?.text}
                    />
                  </MotionDiv>
                )}

                {data?.successTitle && (
                  <MotionDiv
                    style={{ opacity: title.reveal, y: title.y }}
                    className="richtext richtext-start prose-p:font-geist prose-p:text-[4.5rem] prose-p:leading-none prose-p:tracking-[-0.02em] prose-p:text-white lg:prose-p:text-[6rem] prose-strong:font-normal prose-strong:text-white/50 prose-em:font-ivy prose-em:text-[4.875rem] prose-em:tracking-normal prose-em:text-white/50 lg:prose-em:text-[6.5rem]">
                    <PortableText
                      value={data.successTitle}
                      components={richTextComponent}
                    />
                  </MotionDiv>
                )}
              </div>

              {data?.successCTA && (
                <MotionDiv
                  style={{ opacity: cta.reveal, y: cta.y }}
                  className="shrink-0">
                  <CTACornerButton
                    className="lg:w-auto"
                    data={data.successCTA}
                  />
                </MotionDiv>
              )}
            </div>
          </div>

          {/*
            Card strip. Below lg it is a swipeable row; from lg the four cards
            share the content width as a grid, as in the desktop frame.
          */}
          {items && items.length > 0 && (
            <div
              ref={viewportRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerEnd}
              onPointerCancel={onPointerEnd}
              className={cn(
                "scrollbar-hide relative mt-[3.5rem] w-full cursor-grab overflow-x-auto overflow-y-clip pb-[6.25rem] select-none active:cursor-grabbing",
                "lg:mt-[5.625rem] lg:cursor-auto lg:overflow-x-visible lg:pb-[5.25rem] lg:select-auto lg:active:cursor-auto",
              )}>
              <div className="flex w-max gap-[1.75rem] px-[2.625rem] lg:grid lg:w-full lg:grid-cols-4 lg:gap-[2.5rem] lg:px-[12.5rem]">
                {items.map((item, index) => (
                  <SuccessCard
                    key={index}
                    item={item}
                    progress={stripProgress}
                    index={index}
                    total={items.length}
                    reduceMotion={reduceMotion}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
